import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ============================================================================
// COMPONENTES E UI
// ============================================================================
import { Form, notification } from 'components/ds';
import PageContainer from 'modules/account/components/PageContainer/PageContainer';
import ProgressStepper from 'components/ProgressStepper';
import PasswordStrengthChecker from '../../../components/PasswordStrengthChecker/PasswordStrengthChecker';
import TurnstileCaptcha from 'components/TurnstileCaptcha';
import { PasswordInput } from './OnboardingConfigPassword.styles';

// ============================================================================
// HOOKS E SERVIÇOS
// ============================================================================
import { usePassword } from '../../../hooks/usePassword';
import { useGetTemplate } from 'modules/account/configs/SchemaConfig';
import { useRegisterUserLogic } from '../../../hooks/useRegisterUserWorkflow';

// ============================================================================
// UTILITÁRIOS E CONFIGURAÇÕES
// ============================================================================
import { RouteEnum } from '../../../constantes/RouteEnum';
import { isPasswordFormValid } from '../../../constantes/passwordConstants';
import { SCHEMA_STORAGE_KEY } from 'modules/account/services/SchemaService';
import { CreatePasswordRequest } from '../../../_types/password.types';
import { ApiLocalStorage } from '../../../../../utils/ApiLocalStorage';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface PasswordConfig {
  password: string;
  confirmPassword: string;
}

// ============================================================================
// FUNÇÕES AUXILIARES
// ============================================================================

const shouldNavigateToOtpAfterPassword = (): boolean => {
  const schema = ApiLocalStorage.get(SCHEMA_STORAGE_KEY);
  
  if (schema?.requiresOtpAfterPassword) {
    return true;
  }
  
  if (schema?.authenticationType === 'Password' && schema?.otpRequestChannelType) {
    return true;
  }
  
  return false;
};

// ============================================================================
// COMPONENTE
// ============================================================================

export const OnboardingConfigPassword: React.FC = () => {
  const [form] = Form.useForm();
  const [passwordConfig, setPasswordConfig] = useState<PasswordConfig>({
    password: '',
    confirmPassword: '',
  });
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [captchaKey, setCaptchaKey] = useState(0);
  
  const navigate = useNavigate();
  const { createPassword, isLoading: createPasswordLoading } = usePassword();
  const { handleRegisterUser, isRegistering } = useRegisterUserLogic();
  const { data: pageConfig = {} } = useGetTemplate('username');

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    const getGeolocation = async () => {
      try {
        if (navigator.geolocation) {
          const position = await new Promise<GeolocationPosition>(
            (resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                timeout: 10000,
                enableHighAccuracy: true,
              });
            },
          );
          ApiLocalStorage.set('lt', position.coords.latitude.toString());
          ApiLocalStorage.set('lg', position.coords.longitude.toString());
        }
      } catch (error) {
        ApiLocalStorage.set('lt', '');
        ApiLocalStorage.set('lg', '');
      }
    };

    getGeolocation();
  }, []);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleVerify = (token: string) => {
    setCaptchaToken(token);
  };

  const handleFinish = async () => {
    try {
      const values = await form.validateFields();

      const passwordData: CreatePasswordRequest = {
        password: values.password,
        confirmPassword: values.confirmPassword,
        captchaToken,
      };

      const currentParticipantId = ApiLocalStorage.get('@pwa-participant-id')?.replace(/^"|"$/g, '') ||  ApiLocalStorage.get('@pwa-participantId')?.replace(/^"|"$/g, '');
      
      if (!currentParticipantId) {
        try {
          await handleRegisterUser({
            passwordData: {
              password: passwordData.password,
              confirmPassword: passwordData.confirmPassword
            },
            captchaToken
          });
          
          if (shouldNavigateToOtpAfterPassword()) {
            navigate(RouteEnum.OTP_REQUEST);
          } else {
            navigate(RouteEnum.FINISH);
          }
          return;
        } catch (registerError) {
          notification.error({
            message: 'Erro no registro',
            description: 'Não foi possível registrar o usuário. Tente novamente.'
          });
          return;
        }
      } else {
        const success = await createPassword(passwordData);
        
        if (success) {
          if (shouldNavigateToOtpAfterPassword()) {
            navigate(RouteEnum.OTP_REQUEST);
          } else {
            navigate(RouteEnum.FINISH);
          }
        }
      }
    } catch (error) {
      setCaptchaToken('');
      setCaptchaKey((prev) => prev + 1);
      // Erro silencioso - tratamento pode ser adicionado aqui se necessário
    }
  };

  const validatePassword = (value: string, type: string) => {
    setPasswordConfig({
      ...passwordConfig,
      [type]: value,
    });
  };

  // ============================================================================
  // VALIDAÇÕES E REGRAS
  // ============================================================================

  const passwordRules = [
    { required: true, message: 'Por favor, insira sua senha!' },
  ];

  const confirmPasswordRules = [
    { required: true, message: 'Por favor, confirme sua senha!' },
    ({ getFieldValue }: any) => ({
      validator(_: any, value: string) {
        if (!value || getFieldValue('password') === value) {
          return Promise.resolve();
        }

        return Promise.reject(new Error('As senhas não correspondem!'));
      },
    }),
  ];

  // ============================================================================
  // DADOS DERIVADOS
  // ============================================================================

  const isLoading = createPasswordLoading || isRegistering;
  const isButtonDisabled = !isPasswordFormValid(
    passwordConfig?.password,
    passwordConfig?.confirmPassword,
  );

  // ============================================================================
  // RENDERIZAÇÃO
  // ============================================================================

  return (
    <PageContainer
      title={pageConfig?.title || ''}
      navigateBack={pageConfig?.navigateBack?.()}
      buttonText="Criar senha"
      thereIsButton
      onClick={handleFinish}
      loading={isLoading}
      disabled={isLoading || !captchaToken || isButtonDisabled}
    >
      {pageConfig.totalSteps > 1 && (
        <ProgressStepper
          totalSteps={pageConfig.totalSteps}
          currentStep={pageConfig.currentStep}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item name="password" rules={passwordRules}>
          <PasswordInput
            placeholder="Senha"
            size="large"
            onChange={(e) => validatePassword(e.target.value, 'password')}
          />
        </Form.Item>

        <PasswordStrengthChecker password={passwordConfig.password} />

        <Form.Item
          name="confirmPassword"
          dependencies={['password']}
          rules={confirmPasswordRules}
        >
          <PasswordInput
            placeholder="Confirmar senha"
            size="large"
            onChange={(e) =>
              validatePassword(e.target.value, 'confirmPassword')
            }
          />
        </Form.Item>
        <TurnstileCaptcha onVerify={handleVerify} key={captchaKey} />
      </Form>
    </PageContainer>
  );
};
